// Offline play: what a station may do while it cannot reach the cloud, per venue (spec/05 §8; F-SES-23,
// F-SES-24). The owner's decision of 2026-09-30 — "give all as options that can be enabled, configurable
// per venue":
//
//   1. keep running sessions going   a running session plays on past its funded end for up to a venue cap
//                                    (default ON, 5 minutes), then ends; the overrun settles on reconnect
//   2. offline staff codes           staff issue one-time codes from the console (printable); a code starts
//                                    a session for N minutes on any of the venue's stations while offline
//   3. known players                 a player who signed in on this PC recently signs in offline with the
//                                    same password; the time is charged to them on reconnect, capped
//   4. strict lock                   nothing runs past its funded time and nobody new signs in
//
//   GET  /v1/station/offline-policy                                   the station's offline policy + code verifiers
//   POST /v1/station/offline-sessions                                 a session the station ran offline, on reconnect
//   POST /v1/organizations/{o}/venues/{v}/offline-staff-codes         issue codes (plaintext returned once)  sessions:offline_code
//   GET  /v1/organizations/{o}/venues/{v}/offline-staff-codes         the venue's codes (never the digits)  sessions:read
//   POST …/offline-staff-codes/{codeId}/revoke                        revoke an unused code                 sessions:offline_code
//   GET  /v1/organizations/{o}/venues/{v}/offline-sessions            offline sessions, settled or not      sessions:read
//   POST …/offline-sessions/{offlineSessionId}/resolve                settle one that needs attention       sessions:offline_code
//
// **The station never holds a code, only a verifier.** A staff code is 8 random digits the console shows once;
// the server keeps `PBKDF2-HMAC-SHA256(code, salt, iterations)` and hands the station the salt, the hash and the
// iteration count for the venue's live codes, so a station can check a code with no connection and a copy of
// its data folder does not hand out the digits.
import { createRoute } from '@hono/zod-openapi';
import { z } from "../zod.js";
import { MemberIdSchema, OfflineSessionIdSchema, OfflineStaffCodeIdSchema, OrganizationIdSchema, PlayerIdSchema, SessionIdSchema, StationIdSchema, VenueIdSchema, } from "../ids/schema.js";
import { commonProblemResponses, jsonResponse, JSON_CONTENT_TYPE, problemResponse } from "../openapi/responses.js";
import { MoneySchema } from "../primitives/money.js";
import { IsoDateTimeSchema } from "../primitives/scalars.js";
import { VenueScopeParamsSchema } from "../primitives/scope.js";
import { SESSIONS_OFFLINE_CODE, SESSIONS_READ } from "../permissions/index.js";
import { PERMISSION_EXTENSION } from "./audit.js";
const STATION_AUTHENTICATION = 'Station-authenticated: `Authorization: Bearer <device token>` (spec/07 §5). The station provides its ' +
    'own organisation, venue and station from the token.';
/** How many digits an offline staff code has. */
export const OFFLINE_STAFF_CODE_DIGITS = 8;
/** An offline staff code as typed: 8 digits. */
export const OFFLINE_STAFF_CODE_PATTERN = /^\d{8}$/;
/** The most live (issued, unexpired, unused) staff codes a venue may hold — bounds the station's verify cost. */
export const OFFLINE_STAFF_CODE_MAX_LIVE = 200;
/** PBKDF2 iterations for a staff code's verifier. A station checks at most `OFFLINE_STAFF_CODE_MAX_LIVE` of them per try. */
export const OFFLINE_STAFF_CODE_ITERATIONS = 10_000;
// ---------------------------------------------------------------------------------------------
// The station's offline policy
// ---------------------------------------------------------------------------------------------
/**
 * What a staff code does (spec/05 §8.4a, spec/11 §7; F-LCK-09, 2026-10-01): `session` starts a timed session (the
 * original meaning, and the default), `maintenance` puts the PC into maintenance for its minutes (at most 240) under a
 * banner — the offline route into a locked PC. A station never accepts one kind for the other.
 */
export const OfflineStaffCodeKindSchema = z
    .enum(['session', 'maintenance'])
    .openapi('OfflineStaffCodeKind', { description: 'What a staff code does: start a session, or open the PC for maintenance.' });
/** One live staff code, as the station verifies it: never the digits (spec/05 §8.4a). */
export const OfflineStaffCodeVerifierSchema = z
    .object({
    codeId: OfflineStaffCodeIdSchema,
    /** The PBKDF2 salt, base64url (16 bytes). */
    salt: z.string().min(1).max(64),
    /** `PBKDF2-HMAC-SHA256(code, salt, iterations)`, 32 bytes, base64url. */
    hash: z.string().min(1).max(64),
    iterations: z.int().min(1).max(1_000_000),
    /** Minutes of play the code starts. */
    minutes: z.int().min(1).max(480),
    /** After this the station refuses it. */
    expiresAt: IsoDateTimeSchema,
    /** `session` (absent from an older api) or `maintenance`. */
    kind: OfflineStaffCodeKindSchema.optional(),
})
    .openapi('OfflineStaffCodeVerifier');
/**
 * What the station does while it cannot reach the cloud (spec/05 §8.1), resolved for its venue. The station
 * reads it on every connect and every few minutes, keeps the last copy in its data folder, and enforces it
 * while offline. `strictLock` overrides the rest: nothing runs past its funded end and nobody signs in.
 */
export const StationOfflinePolicySchema = z
    .object({
    /** Keep a running session going past its funded end, for up to `graceMinutes` (spec/05 §8.1). */
    continueRunning: z.boolean(),
    /** How far past its funded end a running session may play offline, in minutes (0–240). */
    graceMinutes: z.int().min(0).max(240),
    /** Offline staff codes start a session while offline. */
    staffCodes: z.boolean(),
    /** Known players may sign in offline. */
    knownPlayers: z.boolean(),
    /** The longest a known player's offline session may run, in minutes. */
    knownPlayerMaxMinutes: z.int().min(5).max(480),
    /** How recently a player must have signed in on this PC to sign in offline, in days. */
    knownPlayerWindowDays: z.int().min(1).max(365),
    /** Lock everything when offline: no extension, no offline sign-in. Overrides the others. */
    strictLock: z.boolean(),
    /** The venue's live staff codes, when staff codes are on (empty otherwise). */
    staffCodeVerifiers: z.array(OfflineStaffCodeVerifierSchema).max(OFFLINE_STAFF_CODE_MAX_LIVE),
    /** The api's clock, RFC 3339 UTC. */
    serverTime: IsoDateTimeSchema,
})
    .openapi('StationOfflinePolicy', { description: 'What a station may do while it cannot reach the cloud (spec/05 §8.1).' });
export const getStationOfflinePolicyRoute = createRoute({
    method: 'get',
    path: '/v1/station/offline-policy',
    operationId: 'getStationOfflinePolicy',
    tags: ['sessions', 'stations'],
    summary: 'The station’s offline-play policy and the venue’s staff-code verifiers',
    description: `${STATION_AUTHENTICATION} The venue's offline-play settings as they resolve for this station, and — when ` +
        'staff codes are on — a verifier (salt, PBKDF2 hash) for each live staff code, never the digits. The station ' +
        'keeps the last copy and enforces it while it cannot reach the cloud (spec/05 §8.1, §8.4a).',
    responses: {
        200: jsonResponse(StationOfflinePolicySchema, 'The offline policy.'),
        401: problemResponse('The caller is not an authenticated station.', 'unauthenticated'),
        429: commonProblemResponses[429],
        500: commonProblemResponses[500],
    },
});
// ---------------------------------------------------------------------------------------------
// A session the station ran offline, reported on reconnect (spec/05 §8.5)
// ---------------------------------------------------------------------------------------------
/** How an offline session started. */
export const OfflineSessionKindSchema = z
    .enum(['staff_code', 'known_player'])
    .openapi('OfflineSessionKind', { description: 'How a session the station ran offline started: a staff code, or a known player’s offline sign-in.' });
/** Why an offline session stopped on the station. */
export const OfflineSessionEndSchema = z
    .enum(['logout', 'time_up', 'reconnected'])
    .openapi('OfflineSessionEnd', {
    description: 'Why a session the station ran offline stopped: the player logged out, its minutes ran out, or the connection came back.',
});
export const StationOfflineSessionReportSchema = z
    .object({
    /** The station's own id for the session (a UUID it minted): the report is idempotent on it. */
    localId: z.uuid(),
    kind: OfflineSessionKindSchema,
    /** The staff code that started it (`staff_code`), else null. */
    codeId: OfflineStaffCodeIdSchema.nullable(),
    /** The known player who signed in (`known_player`), else null. */
    playerId: PlayerIdSchema.nullable(),
    /** When it started and stopped on the station's clock (spec/05 §8.3; clamped by the server). */
    startedAt: IsoDateTimeSchema,
    endedAt: IsoDateTimeSchema,
    /** Seconds played, from the station's monotonic clock. */
    playedSeconds: z.int().min(0).max(86_400),
    /** Seconds the station allowed it (the code's minutes, or the known-player cap). */
    allowedSeconds: z.int().min(0).max(86_400),
    endedBy: OfflineSessionEndSchema,
})
    .openapi('StationOfflineSessionReport');
/** Where an offline session stands in its settlement. */
export const OfflineSessionStatusSchema = z
    .enum(['settled', 'needs_attention'])
    .openapi('OfflineSessionStatus', {
    description: '`settled` — charged or resolved; `needs_attention` — staff must settle it (offline play is never silently free).',
});
/** How an offline session was settled. */
export const OfflineSessionResolutionSchema = z
    .enum(['charged', 'paid_at_desk', 'comp'])
    .openapi('OfflineSessionResolution', {
    description: '`charged` — to the player’s wallet or account on reconnect; `paid_at_desk` — staff took payment; `comp` — staff gave it free.',
});
/** Why an offline session needs staff. */
export const OfflineSessionAttentionSchema = z
    .enum(['staff_code', 'code_reused', 'code_unknown', 'player_not_known_here', 'policy_off'])
    .openapi('OfflineSessionAttention', {
    description: 'Why staff must settle it: a staff-code session (collect or comp), a code already used elsewhere, a code the ' +
        'venue never issued, a player who had not signed in on that PC recently, or offline sign-in turned off.',
});
export const OfflineSessionSchema = z
    .object({
    id: OfflineSessionIdSchema,
    organizationId: OrganizationIdSchema,
    venueId: VenueIdSchema,
    stationId: StationIdSchema,
    kind: OfflineSessionKindSchema,
    codeId: OfflineStaffCodeIdSchema.nullable(),
    playerId: PlayerIdSchema.nullable(),
    /** The session it was recorded as (a known player's, charged), else null. */
    sessionId: SessionIdSchema.nullable(),
    startedAt: IsoDateTimeSchema,
    endedAt: IsoDateTimeSchema,
    playedSeconds: z.int().min(0),
    allowedSeconds: z.int().min(0),
    endedBy: OfflineSessionEndSchema,
    /** Seconds charged to the player (a known player's, capped); 0 otherwise. */
    chargedSeconds: z.int().min(0),
    /** What was charged to the player; zero for a staff-code session. */
    charge: MoneySchema,
    status: OfflineSessionStatusSchema,
    attention: OfflineSessionAttentionSchema.nullable(),
    resolution: OfflineSessionResolutionSchema.nullable(),
    note: z.string().max(500).nullable(),
    resolvedAt: IsoDateTimeSchema.nullable(),
    resolvedBy: MemberIdSchema.nullable(),
    reportedAt: IsoDateTimeSchema,
})
    .openapi('OfflineSession', { description: 'A session a station ran offline, as reported on reconnect (spec/05 §8.5).' });
export const reportStationOfflineSessionRoute = createRoute({
    method: 'post',
    path: '/v1/station/offline-sessions',
    operationId: 'reportStationOfflineSession',
    tags: ['sessions', 'stations'],
    summary: 'The station reports a session it ran offline (F-SES-23, F-SES-24)',
    description: `${STATION_AUTHENTICATION} Delivered from the station's durable queue on reconnect. A known player's ` +
        'session is recorded as an ended session and charged — the wallet when it funds play, else the player’s ' +
        'account — capped by the venue’s known-player limit; a staff-code session marks the code used and waits in ' +
        'the console’s offline sessions to be settled. Idempotent on `localId`: a report delivered twice changes ' +
        'nothing more.',
    request: {
        body: { content: { [JSON_CONTENT_TYPE]: { schema: StationOfflineSessionReportSchema } }, required: true },
    },
    responses: {
        200: jsonResponse(OfflineSessionSchema, 'The offline session as recorded.'),
        400: commonProblemResponses[400],
        401: problemResponse('The caller is not an authenticated station.', 'unauthenticated'),
        429: commonProblemResponses[429],
        500: commonProblemResponses[500],
    },
});
// ---------------------------------------------------------------------------------------------
// Offline staff codes, from the console (spec/13 §3.4a)
// ---------------------------------------------------------------------------------------------
export const OfflineStaffCodeStatusSchema = z
    .enum(['live', 'used', 'revoked', 'expired'])
    .openapi('OfflineStaffCodeStatus', { description: 'Whether a staff code can still start a session.' });
export const IssueOfflineStaffCodesSchema = z
    .object({
    /** How many codes to issue at once — a printable sheet. */
    count: z.int().min(1).max(50),
    /** Minutes of play each code starts. */
    minutes: z.int().min(5).max(480),
    /** How long the codes stay usable, in hours. */
    validHours: z.int().min(1).max(720),
    /** Why, for the record ("Internet down, walk-ins"). */
    note: z.string().trim().max(200).optional(),
    /** `session` (the default) or `maintenance` — then `minutes` is the maintenance window, at most 240. */
    kind: OfflineStaffCodeKindSchema.optional(),
})
    .strict()
    .refine((value) => value.kind !== 'maintenance' || value.minutes <= 240, {
    message: 'A maintenance code opens the PC for at most 240 minutes.',
    path: ['minutes'],
})
    .openapi('IssueOfflineStaffCodes');
export const OfflineStaffCodeSchema = z
    .object({
    id: OfflineStaffCodeIdSchema,
    venueId: VenueIdSchema,
    minutes: z.int().min(1),
    expiresAt: IsoDateTimeSchema,
    status: OfflineStaffCodeStatusSchema,
    issuedAt: IsoDateTimeSchema,
    issuedBy: MemberIdSchema.nullable(),
    note: z.string().nullable(),
    usedAt: IsoDateTimeSchema.nullable(),
    usedStationId: StationIdSchema.nullable(),
    kind: OfflineStaffCodeKindSchema,
})
    .openapi('OfflineStaffCode', { description: 'An offline staff code: never its digits, which are shown once when issued.' });
/** A code just issued: the digits, shown once for the printable sheet. */
export const IssuedOfflineStaffCodeSchema = z
    .object({
    id: OfflineStaffCodeIdSchema,
    code: z.string().regex(OFFLINE_STAFF_CODE_PATTERN).openapi({ example: '40817263' }),
    minutes: z.int().min(1),
    expiresAt: IsoDateTimeSchema,
})
    .openapi('IssuedOfflineStaffCode');
export const IssuedOfflineStaffCodesSchema = z
    .object({ codes: z.array(IssuedOfflineStaffCodeSchema) })
    .openapi('IssuedOfflineStaffCodes', { description: 'The codes just issued. The digits are never returned again.' });
export const OfflineStaffCodeListSchema = z.object({ items: z.array(OfflineStaffCodeSchema) }).openapi('OfflineStaffCodeList');
export const OfflineSessionListSchema = z.object({ items: z.array(OfflineSessionSchema) }).openapi('OfflineSessionList');
export const ListOfflineSessionsQuerySchema = z
    .object({ status: OfflineSessionStatusSchema.optional() })
    .openapi('ListOfflineSessionsQuery');
export const ResolveOfflineSessionSchema = z
    .object({
    resolution: z.enum(['paid_at_desk', 'comp']),
    note: z.string().trim().max(500).optional(),
})
    .strict()
    .openapi('ResolveOfflineSession');
const forbidden = problemResponse('Your role does not allow this.', 'forbidden', 'second-factor-required');
const venueNotFound = problemResponse('No such venue in this organisation, or not one you can see.', 'not-found');
const CodeParamsSchema = z
    .object({ organizationId: OrganizationIdSchema, venueId: VenueIdSchema, codeId: OfflineStaffCodeIdSchema })
    .openapi('OfflineStaffCodeParams');
const OfflineSessionParamsSchema = z
    .object({ organizationId: OrganizationIdSchema, venueId: VenueIdSchema, offlineSessionId: OfflineSessionIdSchema })
    .openapi('OfflineSessionParams');
export const issueOfflineStaffCodesRoute = createRoute({
    method: 'post',
    path: '/v1/organizations/{organizationId}/venues/{venueId}/offline-staff-codes',
    operationId: 'issueOfflineStaffCodes',
    tags: ['sessions'],
    summary: 'Issue offline staff codes',
    description: 'Issue one-time codes that start a session of `minutes` on any of the venue’s stations while they cannot reach ' +
        'the cloud (spec/05 §8.4a). The digits are returned once, for the printable sheet; only a salted hash is kept. ' +
        'A venue holds at most 200 live codes. Requires `sessions:offline_code`. Audited.',
    [PERMISSION_EXTENSION]: SESSIONS_OFFLINE_CODE,
    request: {
        params: VenueScopeParamsSchema,
        body: { content: { [JSON_CONTENT_TYPE]: { schema: IssueOfflineStaffCodesSchema } }, required: true },
    },
    responses: {
        201: jsonResponse(IssuedOfflineStaffCodesSchema, 'The codes, shown once.'),
        400: commonProblemResponses[400],
        401: commonProblemResponses[401],
        403: forbidden,
        404: venueNotFound,
        409: problemResponse('The venue already holds the most live codes it may.', 'conflict'),
        429: commonProblemResponses[429],
        500: commonProblemResponses[500],
    },
});
export const listOfflineStaffCodesRoute = createRoute({
    method: 'get',
    path: '/v1/organizations/{organizationId}/venues/{venueId}/offline-staff-codes',
    operationId: 'listOfflineStaffCodes',
    tags: ['sessions'],
    summary: 'List the venue’s offline staff codes',
    description: 'The venue’s offline staff codes, newest first — never their digits. Requires `sessions:read`.',
    [PERMISSION_EXTENSION]: SESSIONS_READ,
    request: { params: VenueScopeParamsSchema },
    responses: {
        200: jsonResponse(OfflineStaffCodeListSchema, 'The codes.'),
        400: commonProblemResponses[400],
        401: commonProblemResponses[401],
        403: forbidden,
        404: venueNotFound,
        429: commonProblemResponses[429],
        500: commonProblemResponses[500],
    },
});
export const revokeOfflineStaffCodeRoute = createRoute({
    method: 'post',
    path: '/v1/organizations/{organizationId}/venues/{venueId}/offline-staff-codes/{codeId}/revoke',
    operationId: 'revokeOfflineStaffCode',
    tags: ['sessions'],
    summary: 'Revoke an offline staff code',
    description: 'Revoke a code that has not been used: stations drop it the next time they read their offline policy. A code ' +
        'already used or revoked is returned as it is. Requires `sessions:offline_code`. Audited.',
    [PERMISSION_EXTENSION]: SESSIONS_OFFLINE_CODE,
    request: { params: CodeParamsSchema },
    responses: {
        200: jsonResponse(OfflineStaffCodeSchema, 'The code, revoked.'),
        400: commonProblemResponses[400],
        401: commonProblemResponses[401],
        403: forbidden,
        404: problemResponse('No such code at this venue.', 'not-found'),
        429: commonProblemResponses[429],
        500: commonProblemResponses[500],
    },
});
export const listOfflineSessionsRoute = createRoute({
    method: 'get',
    path: '/v1/organizations/{organizationId}/venues/{venueId}/offline-sessions',
    operationId: 'listOfflineSessions',
    tags: ['sessions'],
    summary: 'List the sessions the venue’s stations ran offline',
    description: 'Newest first; `status=needs_attention` for the ones staff must settle. Requires `sessions:read`.',
    [PERMISSION_EXTENSION]: SESSIONS_READ,
    request: { params: VenueScopeParamsSchema, query: ListOfflineSessionsQuerySchema },
    responses: {
        200: jsonResponse(OfflineSessionListSchema, 'The offline sessions.'),
        400: commonProblemResponses[400],
        401: commonProblemResponses[401],
        403: forbidden,
        404: venueNotFound,
        429: commonProblemResponses[429],
        500: commonProblemResponses[500],
    },
});
export const resolveOfflineSessionRoute = createRoute({
    method: 'post',
    path: '/v1/organizations/{organizationId}/venues/{venueId}/offline-sessions/{offlineSessionId}/resolve',
    operationId: 'resolveOfflineSession',
    tags: ['sessions'],
    summary: 'Settle an offline session that needs attention',
    description: 'Record how staff settled a session a station ran offline: payment taken at the desk (through a sale), or given ' +
        'free. Moves no money itself. A settled one is returned as it is. Requires `sessions:offline_code`. Audited.',
    [PERMISSION_EXTENSION]: SESSIONS_OFFLINE_CODE,
    request: {
        params: OfflineSessionParamsSchema,
        body: { content: { [JSON_CONTENT_TYPE]: { schema: ResolveOfflineSessionSchema } }, required: true },
    },
    responses: {
        200: jsonResponse(OfflineSessionSchema, 'The offline session, settled.'),
        400: commonProblemResponses[400],
        401: commonProblemResponses[401],
        403: forbidden,
        404: problemResponse('No such offline session at this venue.', 'not-found'),
        429: commonProblemResponses[429],
        500: commonProblemResponses[500],
    },
});
//# sourceMappingURL=offline-play.js.map